// TODO: Is there a way to share this code across the other status dropdowns?

import { Dispatch, SetStateAction, useState } from "react";
import clsx from "clsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDownIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { useUpdateCandidacy } from "@/hooks/mutations";
import { useSetCandidacyInCandidacies } from "@/hooks/sets";
import {
  candidacyStatusOptions as allCandidacyStatusOptions,
  candidacyStatusOptionsExRejected,
  candidacyStatusToBackgroundColor,
  candidacyStatusToLabel,
} from "@/lib/constants";
import { Candidacy, CandidacyStatus } from "@/types";

const jobPipelineCandidateSheetStatusDropdownSchema = z.object({
  status: z.nativeEnum(CandidacyStatus, {
    required_error: "Required",
  }),
});

export const JobPipelineCandidateSheetStatusDropdown = ({
  candidacy,
  setSelectedCandidacyIndex,
}: {
  candidacy: Candidacy;
  setSelectedCandidacyIndex: Dispatch<SetStateAction<number | null>>;
}) => {
  const [open, setOpen] = useState(false);
  const setCandidacyInCandidacies = useSetCandidacyInCandidacies();
  const { mutate: updateCandidacy, status } = useUpdateCandidacy();

  const candidacyStatusOptions =
    candidacy.status === CandidacyStatus.REJECTED
      ? allCandidacyStatusOptions
      : candidacyStatusOptionsExRejected;

  const form = useForm<
    z.infer<typeof jobPipelineCandidateSheetStatusDropdownSchema>
  >({
    defaultValues: {
      status: candidacy.status,
    },
    resolver: zodResolver(jobPipelineCandidateSheetStatusDropdownSchema),
  });

  const handleSubmit = (
    data: z.infer<typeof jobPipelineCandidateSheetStatusDropdownSchema>,
  ) => {
    if (data.status === candidacy.status) {
      // Close the dropdown and do nothing since the status didn't change.
      setOpen(false);
      return;
    }

    updateCandidacy(
      {
        candidacyId: candidacy.id,
        updateCandidacyRequest: {
          status: data.status,
        },
      },
      {
        onSuccess: () => {
          // Close the dropdown.
          setOpen(false);

          // Close the sheet for now.
          setSelectedCandidacyIndex(null);

          // Ensure that the candidacy is updated in the candidacies cache.
          setCandidacyInCandidacies({
            candidacy: { ...candidacy, status: data.status },
            timeInStage: new Date().toISOString(),
          });

          // https://github.com/shadcn-ui/ui/issues/1912#issuecomment-2187447622
          setTimeout(() => {
            const body = document.querySelector("body");
            if (body) {
              body.style.pointerEvents = "";
            }
          }, 100);
        },
      },
    );
  };

  return (
    <DropdownMenu onOpenChange={setOpen} open={open}>
      <DropdownMenuTrigger asChild>
        <Button className="rounded-full" variant="outlineSecondary">
          <div
            className={clsx(
              "h-3 w-3 rounded-full",
              candidacyStatusToBackgroundColor[candidacy.status],
            )}
          />
          {candidacyStatusToLabel[candidacy.status]}
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[250px] p-0">
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem className="p-4 text-slate-700">
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-y-1"
                    >
                      {candidacyStatusOptions.map((candidacyStatusOption) => (
                        <FormItem
                          key={candidacyStatusOption.value}
                          className="flex gap-3 space-y-0"
                        >
                          <FormControl>
                            <RadioGroupItem
                              value={candidacyStatusOption.value}
                            />
                          </FormControl>
                          <FormLabel className="font-normal">
                            {candidacyStatusOption.label}
                          </FormLabel>
                        </FormItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex justify-end border-t border-slate-200 p-2">
              <Button
                disabled={status === "pending"}
                type="submit"
                variant="ghost"
              >
                Apply
              </Button>
            </div>
          </form>
        </Form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
